/**
 * Les pastilles d'état : celle d'une fiche enfant, celle d'une inscription.
 *
 * Elles vivent ici, ensemble, parce qu'elles doivent se ressembler sans se
 * confondre. Un parent qui voit trois couleurs sur un même écran doit pouvoir
 * apprendre une seule fois ce qu'elles veulent dire : vert, rien à faire ;
 * ambre, quelque chose à faire ; rouge, une place perdue.
 *
 * Aucune n'est un simple point de couleur : chacune porte son mot. Une pastille
 * muette oblige à deviner, et 8 % des hommes ne distinguent pas le vert du
 * rouge.
 */

import { AlertCircle, Check, Clock, X } from 'lucide-react';
import clsx from 'clsx';
import type { EtatFiche } from '../../lib/completude';
import type { StatutAffiche } from '../../lib/inscriptions/statut';

const TONS = {
  vert: 'bg-green-50 text-green-800 ring-green-200',
  ambre: 'bg-amber-50 text-amber-900 ring-amber-200',
  rouge: 'bg-red-50 text-red-800 ring-red-200',
} as const;

const Pastille = ({
  ton,
  icone: Icone,
  children,
}: {
  ton: keyof typeof TONS;
  icone: typeof Check;
  children: React.ReactNode;
}) => (
  <span
    className={clsx(
      'inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium ring-1 ring-inset',
      TONS[ton],
    )}
  >
    <Icone className="h-3.5 w-3.5 flex-shrink-0" aria-hidden="true" />
    {children}
  </span>
);

/** L'état d'une fiche enfant. `resume` porte le compte des manques. */
export const PastilleFiche = ({ etat, resume }: { etat: EtatFiche; resume: string }) => {
  if (etat === 'a-completer') {
    return (
      <Pastille ton="ambre" icone={AlertCircle}>
        {resume}
      </Pastille>
    );
  }
  if (etat === 'a-verifier') {
    return (
      <Pastille ton="ambre" icone={Clock}>
        {resume}
      </Pastille>
    );
  }
  return (
    <Pastille ton="vert" icone={Check}>
      {resume}
    </Pastille>
  );
};

/** L'état d'une inscription : validée, annulation demandée, annulée. */
export const PastilleInscription = ({ statut }: { statut: StatutAffiche }) => {
  const icone = statut.cle === 'annulee' ? X : statut.cle === 'demande' ? Clock : Check;
  return (
    <Pastille ton={statut.ton} icone={icone}>
      {statut.libelle}
    </Pastille>
  );
};

export default Pastille;
